<template>
  <div class="signin-page-container">
    <div class="container d-flex align-items-center justify-content-center min-vh-100 py-5">
      <div class="login-card shadow-premium" id="containLoader" ref="containLoader">
        <!-- Left Panel: Brand & info -->
        <div class="brand-panel">
          <div class="brand-panel-overlay"></div>
          <div class="brand-panel-content">
            <router-link :to="{ name: 'Home' }" class="brand-logo-link">
              <img
                class="brand-logo img-fluid animate-logo"
                alt="SECC Logo"
                src="../../assets/secclogo.png"
              />
            </router-link>
            <h3 class="welcome-title font-moul">{{ $t("message.welcome") }}</h3>
            <p class="welcome-subtitle">{{ $t("message.welcome_training") }}</p>
            <p class="welcome-text">{{ $t("message.keep_connection") }}</p>

            <div class="camdigikey-info-box">
              <span class="support-label">{{ $t("message.support_with") }}</span>
              <a href="https://camdigikey.gov.kh/" target="_blank" class="camdigikey-logo-link">
                <img src="@/assets/images/unnamed.webp" class="camdigikey-badge-img" alt="CamDigiKey" />
              </a>
              <div class="download-apps-section w-100">
                <p class="download-label text-center">{{ $t("message.download_app") }}</p>
                <div class="download-links justify-content-center">
                  <a
                    href="https://play.google.com/store/apps/details?id=kh.gov.camdx.camdigikey"
                    target="_blank"
                    class="btn-download btn-android"
                  >
                    <i class="fab fa-android"></i> Android
                  </a>
                  <a
                    href="https://apps.apple.com/us/app/camdigikey/id1527303038"
                    target="_blank"
                    class="btn-download btn-ios"
                  >
                    <i class="fab fa-apple"></i> iOS
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Form Panel -->
        <div class="form-panel">
          <div class="form-panel-content">
            <!-- Screen 0: Enter Email -->
            <div v-if="forgetpassword.setScreen == 0" class="animate-fade-in">
              <div class="icon-header-badge">
                <i class="fas fa-key"></i>
              </div>
              <h3 class="form-title text-center mb-2">Forget Password?</h3>
              <p class="text-muted text-center mb-4" style="font-size: 0.88rem;">
                Enter your registered email address to reset password
              </p>

              <form @submit.prevent="sendemail">
                <div class="form-group-custom">
                  <label class="form-label-custom">Email Address</label>
                  <div class="input-wrapper">
                    <i class="fas fa-envelope input-icon"></i>
                    <input
                      type="text"
                      v-model="forgetpassword.email"
                      :class="{ 'is-invalid': v$.forgetpassword.email.$error }"
                      class="form-control-custom"
                      placeholder="Enter your email"
                    />
                  </div>
                  <div v-if="v$.forgetpassword.email.$error" class="invalid-feedback d-block mt-1">
                    Please enter a valid email address.
                  </div>
                </div>

                <button type="submit" class="btn-premium-signin mt-3">
                  Reset Password <i class="fas fa-paper-plane ms-2"></i>
                </button>

                <div class="mt-4 text-center">
                  <span class="text-muted" style="font-size: 0.85rem;">Already have an account? </span>
                  <router-link :to="{ name: 'SystemLogin' }" class="forget-password-link">
                    Sign In
                  </router-link>
                </div>
              </form>
            </div>

            <!-- Screen 1: Enter OTP Confirmation -->
            <div v-else-if="forgetpassword.setScreen == 1" class="animate-fade-in">
              <div class="icon-header-badge">
                <i class="fas fa-shield-alt"></i>
              </div>
              <h3 class="form-title text-center mb-2">OTP Verification</h3>
              <p class="text-muted text-center mb-4" style="font-size: 0.88rem;">
                Please check your email and enter the confirmation code.
              </p>

              <form @submit.prevent="confirmCode">
                <div class="form-group-custom">
                  <label class="form-label-custom">Confirmation Code</label>
                  <div class="input-wrapper">
                    <i class="fas fa-lock input-icon"></i>
                    <input
                      v-model="confirm.otp"
                      type="text"
                      style="text-align: center; letter-spacing: 4px; font-weight: bold;"
                      :class="{ 'is-invalid': v$.confirm.otp.$error }"
                      class="form-control-custom"
                      placeholder="123456"
                      maxlength="6"
                    />
                  </div>
                  <div v-if="v$.confirm.otp.$error" class="invalid-feedback d-block mt-1">
                    Please enter your confirmation code.
                  </div>
                </div>

                <button type="submit" class="btn-premium-signin mt-3">
                  Confirm Code <i class="fas fa-check-circle ms-2"></i>
                </button>

                <div class="mt-4 text-center" style="font-size: 0.85rem; line-height: 1.6;">
                  <span class="text-muted">Didn't receive the code? </span>
                  <a href="#" class="forget-password-link" @click.prevent="sendemail">Resend Code</a>
                  <div class="mt-2">
                    <router-link :to="{ name: 'SystemLogin' }" class="forget-password-link">
                      Back to Sign In
                    </router-link>
                  </div>
                </div>
              </form>
            </div>

            <!-- Screen 2: Reset Password Form -->
            <div v-else-if="forgetpassword.setScreen == 2 && forgetpassword.isverify" class="animate-fade-in">
              <div class="icon-header-badge">
                <i class="fas fa-lock-open"></i>
              </div>
              <h3 class="form-title text-center mb-2">New Password</h3>
              <p class="text-muted text-center mb-4" style="font-size: 0.88rem;">
                Choose a strong, secure new password.
              </p>

              <form @submit.prevent="resetpassword">
                <div class="form-group-custom">
                  <label class="form-label-custom">New Password</label>
                  <div class="input-wrapper">
                    <i class="fas fa-lock input-icon"></i>
                    <input
                      type="password"
                      v-model="reset.password"
                      :class="{ 'is-invalid': v$.reset.password.$error }"
                      class="form-control-custom"
                      placeholder="Enter new password"
                    />
                  </div>
                  <div v-if="v$.reset.password.$error" class="invalid-feedback d-block mt-1">
                    Password is required.
                  </div>
                </div>

                <div class="form-group-custom">
                  <label class="form-label-custom">Confirm Password</label>
                  <div class="input-wrapper">
                    <i class="fas fa-check-double input-icon"></i>
                    <input
                      type="password"
                      v-model="reset.password_confirmation"
                      :class="{ 'is-invalid': v$.reset.password_confirmation.$error }"
                      class="form-control-custom"
                      placeholder="Confirm new password"
                    />
                  </div>
                  <div v-if="v$.reset.password_confirmation.$error" class="invalid-feedback d-block mt-1">
                    Passwords must match.
                  </div>
                </div>

                <button type="submit" class="btn-premium-signin mt-3">
                  Reset Password <i class="fas fa-sync-alt ms-2"></i>
                </button>

                <div class="mt-4 text-center">
                  <router-link :to="{ name: 'SystemLogin' }" class="forget-password-link">
                    Back to Sign In
                  </router-link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";
import useVuelidate from "@vuelidate/core";
import { required, email, sameAs } from "@vuelidate/validators";

export default {
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      forgetpassword: {
        email: "",
        isverify: true,
        setScreen: 0,
      },
      confirm: {
        otp: "",
      },
      reset: {
        password: "",
        password_confirmation: "",
      }
    };
  },
  validations() {
    return {
      forgetpassword: {
        email: { required, email, $autoDirty: true },
      },
      confirm: {
        otp: { required, $autoDirty: true },
      },
      reset: {
        password: { required, $autoDirty: true },
        password_confirmation: {
          required,
          sameAsPassword: sameAs(this.reset.password),
          $autoDirty: true
        }
      }
    };
  },
  methods: {
    showLoader() {
      return this.$loading.show({
        container: this.$refs.containLoader,
        color: "#01B201",
        canCancel: true,
        onCancel: this.onCancelLoader
      });
    },
    async sendemail() {
      this.v$.forgetpassword.$touch();
      if (!this.v$.forgetpassword.email.$invalid) {
        let loader = this.showLoader();
        try {
          let response = await axios.post("sent-reset-password", {
            email: this.forgetpassword.email
          });
          this.$toast.open({
            message: response.data.message,
            type: "success",
            duration: 5000,
            dismissible: true,
            position: "top-right"
          });
          loader.hide();
          this.forgetpassword.setScreen = 1;
        } catch (error) {
          this.$toast.open({
            message: error.response.data.message,
            type: "error",
            duration: 5000,
            dismissible: true,
            position: "top-right"
          });
          loader.hide();
        }
      }
    },
    async confirmCode() {
      this.v$.confirm.$touch();
      if (!this.v$.confirm.$invalid) {
        let loader = this.showLoader();
        try {
          let response = await axios.post("verify-opt-code", {
            email: this.forgetpassword.email,
            otp: this.confirm.otp
          });
          this.$toast.open({
            message: response.data.message,
            type: "success",
            duration: 5000,
            dismissible: true,
            position: "top-right"
          });
          loader.hide();
          this.forgetpassword.setScreen = 2;
          this.forgetpassword.isverify = true;
        } catch (error) {
          this.$toast.open({
            message: error.response.data.message,
            type: "error",
            duration: 5000,
            dismissible: true,
            position: "top-right"
          });
          loader.hide();
        }
      }
    },
    async resetpassword() {
      this.v$.reset.$touch();
      if (!this.v$.reset.$invalid) {
        let loader = this.showLoader();
        try {
          let response = await axios.post("save-reset-password", {
            email: this.forgetpassword.email,
            otp: this.confirm.otp,
            password: this.reset.password,
            password_confirmation: this.reset.password_confirmation
          });
          this.$toast.open({
            message: response.data.message,
            type: "success",
            duration: 5000,
            dismissible: true,
            position: "top-right"
          });
          loader.hide();
          this.forgetpassword.setScreen = 0;
          this.forgetpassword.isverify = false;
          this.$router.push({ name: "SystemLogin" });
        } catch (error) {
          this.$toast.open({
            message: error.response.data.message,
            type: "error",
            duration: 5000,
            dismissible: true,
            position: "top-right"
          });
          this.forgetpassword.setScreen = 0;
          this.forgetpassword.isverify = false;
          loader.hide();
          this.$router.push({ name: "ForgetPassword" });
        }
      }
    }
  }
};
</script>

<style scoped>
.signin-page-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #0e8937 0%, #05170d 100%);
  font-family: 'Battambang', 'Inter', sans-serif;
}

/* Login Card Layout */
.login-card {
  display: flex;
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 950px;
  min-height: 580px;
}

/* Left Brand Panel */
.brand-panel {
  position: relative;
  flex: 1;
  background: url("~@/assets/images/bg.jpg") no-repeat center center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #ffffff;
}

.brand-panel-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(9, 26, 16, 0.65) 0%,
    rgba(5, 12, 8, 0.78) 100%
  );
  z-index: 1;
}

.brand-panel-content {
  position: relative;
  z-index: 2;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.brand-logo-link {
  display: inline-block;
  margin-bottom: 24px;
}

.brand-logo {
  width: 110px;
  height: auto;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.brand-logo:hover {
  transform: rotate(-3deg) scale(1.08);
}

.welcome-title {
  font-family: 'Moul', 'Khmer Moul', cursive;
  font-size: 1.6rem;
  color: #ffc107;
  margin-bottom: 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 0 10px rgba(255, 193, 7, 0.2);
}

.welcome-subtitle {
  font-size: 1.05rem;
  font-weight: 500;
  margin-bottom: 12px;
  color: #ffffff;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.8);
}

.welcome-text {
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 30px;
  max-width: 280px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
}

/* CamDigiKey download section */
.camdigikey-info-box {
  width: 100%;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.support-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 12px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.camdigikey-badge-img {
  width: 75px;
  border-radius: 10px;
  transition: transform 0.2s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  margin-bottom: 20px;
}

.camdigikey-badge-img:hover {
  transform: scale(1.05);
}

.download-apps-section {
  margin-top: 10px;
}

.download-label {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 12px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.download-links {
  display: flex;
  gap: 12px;
}

.btn-download {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 8px 16px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 30px;
  text-decoration: none;
  transition: all 0.2s;
  color: #ffffff;
}

.btn-download:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-1px);
}

.btn-android:hover {
  border-color: #3ddc84;
  box-shadow: 0 0 10px rgba(61, 220, 132, 0.2);
}

.btn-ios:hover {
  border-color: #ffffff;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
}

/* Right Form Panel */
.form-panel {
  flex: 1.2;
  padding: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
}

.form-panel-content {
  width: 100%;
  max-width: 380px;
}

.icon-header-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  background: rgba(14, 137, 55, 0.08);
  border-radius: 50%;
  color: #0e8937;
  font-size: 26px;
  margin: 0 auto 20px auto;
  border: 1px solid rgba(14, 137, 55, 0.15);
}

.form-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e35;
}

/* Custom Input Controls */
.form-group-custom {
  margin-bottom: 20px;
}

.form-label-custom {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #4a5c50;
  margin-bottom: 6px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  color: #8c9c90;
  font-size: 0.95rem;
}

.form-control-custom {
  width: 100%;
  padding: 12px 16px 12px 42px;
  background: #f4f5f7;
  border: 1.5px solid #edf0f2;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #2c3e35;
  transition: all 0.25s ease;
}

.form-control-custom:focus {
  background: #ffffff;
  border-color: #0e8937;
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 137, 55, 0.1);
}

.form-control-custom.is-invalid {
  border-color: #dc3545;
  background-color: #fdf3f4;
}

.form-control-custom.is-invalid:focus {
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.forget-password-link {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0e8937;
  text-decoration: none;
  transition: color 0.2s;
}

.forget-password-link:hover {
  color: #0a5c2c;
  text-decoration: underline;
}

/* Action Buttons styling */
.btn-premium-signin {
  position: relative;
  width: 100%;
  background: linear-gradient(135deg, #0e8937 0%, #0a5c2c 100%);
  color: #ffffff;
  border: none;
  padding: 12px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 15px rgba(14, 137, 55, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-premium-signin:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(14, 137, 55, 0.3);
  background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
  color: #050b07;
}

/* Animations */
.animate-fade-in {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-logo {
  animation: bounceSubtle 3s infinite ease-in-out;
}

@keyframes bounceSubtle {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

/* Responsive Rules */
@media (max-width: 820px) {
  .login-card {
    flex-direction: column;
    max-width: 450px;
    border-radius: 16px;
  }
  
  .brand-panel {
    padding: 30px 20px;
  }
  
  .form-panel {
    padding: 40px 30px;
  }
}
</style>
